import React from "react";
import { Link, useLocation } from "react-router-dom";
import LeagueTypeIcon from "./LeagueTypeIcon";
import { ChevronRightIcon } from "./icons";
import { standaloneLeaderboardPath } from "../../../utils/leaguePath";
import { rememberListPosition } from "../../../utils/listPosition";
import {
  assetLabel,
  finalRank,
  finalReturn,
  historyRangeLabel,
  leagueTypeLabel,
  metaLine,
  returnDisplay,
} from "./leagueFormat";

// A league that has finished, as it appears under My Leagues.
//
// The hierarchy is the point here: the type line is the quietest thing on the
// card, the name is the loudest, and the two numbers the user came back for —
// where they placed and what they made — carry the only colour. Gold reads as a
// placing; the return is directional, so it stays green or red and always signed.
//
// Rank and return only render when the payload carries them; a completed league
// with neither still shows its identity and a way back to the results.
//
// ── Two client changes, and what they changed ─────────────────────────────
//
// 1. "Make the full card clickable, not just the View Results and chevron."
//    The card was a <div> with a <button> in its corner, so ~90% of its surface
//    did nothing. It is a <Link> now and "View Results ›" is what it always
//    should have been — the AFFORDANCE that says the card opens, not the only
//    part of it that does.
//
// 2. "I would reduce the overall card height by roughly 12% to 15%. Do not
//    shrink the typography." So every value below is spacing, and every type
//    size, weight and colour is exactly what it was. The four reductions the
//    client itemised, in their words:
//
//      "Reduce the top and bottom padding by about 6 to 8 pixels each."
//          p-4 md:p-5 -> py-2.5 md:py-3.5 (16->10px, 20->14px). Horizontal
//          padding is unchanged; only the vertical was called out.
//      "Tighten the space between the league type, title, and dates."
//          title mt-2 -> mt-1 (8->4px), dates mt-1 -> mt-0.5 (4->2px)
//      "Reduce the space above and below the divider slightly."
//          mt-4 pt-4 -> mt-3.5 pt-3.5 (16->14px on each side) -- "slightly"
//      "Reduce the large gap between the result values and 'View Results' by
//       about 10 to 12 pixels."
//          pt-4 -> pt-1.5 (16->6px, the 10px end of the range)
//
//    The two ranges are taken at their gentler end deliberately. Read at their
//    strongest (8px of padding, 12px on the footer, 4px either side of the
//    divider) the itemised cuts come to 38px off a 226px card -- 17%, past the
//    12-15% the client set as the headline. The values above come to 32px, or
//    14.2%, which satisfies both halves of the instruction. Measured, not
//    estimated: see the numbers in the handover.
//
//    "Keep the divider and the two-column results layout. Those give the card
//    its hierarchy." — both are untouched.
//
// 3. "'View Results' CTA from any completed card must open the final leaderboard
//    directly - not take people into the league home or the league shell." It
//    used to navigate to /contest/<slug>, which is the league's home screen
//    inside the shell. standaloneLeaderboardPath is the leaderboard on its own,
//    still inside the main QUSHi site.
const GOLD = "#D7C588";

const CompletedLeagueTile = ({ game }) => {
  const { pathname } = useLocation();
  const rank = finalRank(game);
  const ret = finalReturn(game);
  // Sign and colour come from the one sitewide decision, so this card can't
  // paint a negative return green or put a minus on a 0.0%.
  const retDisplay = returnDisplay(ret);
  const hasNumbers = rank !== null || retDisplay !== null;

  const typeLine = metaLine(leagueTypeLabel(game), assetLabel(game));
  const dates = historyRangeLabel(game);

  return (
    <Link
      to={standaloneLeaderboardPath(game?.vanity_url)}
      /* Client: "it takes you back to the same place in the list. The same
         completed competition you originally clicked on." The card records
         itself on the way out; the list screen scrolls it back into view when
         the back arrow returns. See utils/listPosition.js. */
      data-list-item={game?.id}
      onClick={() => rememberListPosition(pathname, game?.id)}
      className="group flex h-full flex-col rounded-2xl border border-gray-200 bg-white px-4 py-2.5 transition-colors hover:bg-[#F8FAFA] md:px-5 md:py-3.5"
    >
      <p className="flex items-center gap-2 text-sm font-semibold text-secondary">
        <LeagueTypeIcon game={game} className="w-4 h-4 shrink-0" />
        <span className="truncate">{typeLine}</span>
      </p>

      <h3 className="mt-1 font-montserrat font-bold text-lg text-[#0F172A] leading-snug">
        {game?.title}
      </h3>
      {dates && <p className="mt-0.5 text-sm text-secondary">{dates}</p>}

      {hasNumbers && (
        <div className="mt-3.5 pt-3.5 border-t border-divider flex items-start justify-between gap-4">
          <div>
            <p className="text-sm text-secondary">Final standing</p>
            <p
              className="mt-0.5 font-montserrat font-bold text-2xl leading-none"
              style={{ color: GOLD }}
            >
              {rank !== null ? `#${rank}` : "—"}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-secondary">Your return</p>
            <p
              className={`mt-0.5 font-montserrat font-bold text-2xl leading-none ${
                retDisplay ? retDisplay.cls : "text-secondary"
              }`}
            >
              {retDisplay ? retDisplay.text : "—"}
            </p>
          </div>
        </div>
      )}

      <div className="mt-auto pt-1.5 flex justify-end">
        <span className="inline-flex items-center gap-1 font-semibold text-sm md:text-base text-[#0F4146] group-hover:underline">
          View Results
          <ChevronRightIcon className="h-4 w-4 md:h-5 md:w-5" />
        </span>
      </div>
    </Link>
  );
};

export default CompletedLeagueTile;
